import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, DollarSign, TrendingUp, UserPlus, Mail, MessageCircle, Settings, Tag, Pause, Archive, Play, MessageSquare, CreditCard, ChevronDown, ChevronUp, LinkIcon } from 'lucide-react';
import { trainerApi, offerApi, chatApi, adminApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

const getWhatsAppUrl = (phone) => {
  if (!phone) return null;
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) cleaned = '91' + cleaned.slice(1);
  if (cleaned.length === 10) cleaned = '91' + cleaned;
  return `https://wa.me/${cleaned}`;
};

function TrainerDashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offers, setOffers] = useState([]);
  const [showL1Settings, setShowL1Settings] = useState(false);
  const [l1Config, setL1Config] = useState({ trial_days: 7, daily_limit: 5 });
  const [isSavingL1, setIsSavingL1] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, studentsRes, l1Res, offersRes, quotaRes] = await Promise.all([
        trainerApi.getDashboard(),
        trainerApi.getStudents(),
        trainerApi.getL1Config(),
        offerApi.list(),
        chatApi.getQuota().catch(() => ({ data: null }))
      ]);
      setStats(dashRes.data);
      setStudents(studentsRes.data.students);
      setL1Config({ trial_days: l1Res.data.trial_days, daily_limit: l1Res.data.daily_limit });
      setOffers(offersRes.data);
      setQuota(quotaRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveL1Config = async () => {
    setIsSavingL1(true);
    try {
      await trainerApi.updateL1Config(l1Config);
      toast.success('L1 settings saved');
      setShowL1Settings(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save L1 settings');
    } finally {
      setIsSavingL1(false);
    }
  };

  const handleGrantL1 = async (studentId) => {
    try {
      await trainerApi.grantL1Trial(studentId);
      toast.success('L1 trial granted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to grant trial');
    }
  };

  const handleStudentStatus = async (studentId, status) => {
    const label = status === 'paused' ? 'Pause' : status === 'archived' ? 'Pause & Archive' : 'Reactivate';
    if (!confirm(`${label} this student?`)) return;
    try {
      await trainerApi.updateStudentStatus(studentId, status);
      toast.success(`Student ${status === 'active' ? 'reactivated' : status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update student status');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome, {user?.name}</h1>
          <p className="text-sm text-gray-500">{user?.role === 'admin' ? 'Admin Dashboard' : 'Trainer Dashboard'}</p>
        </div>
        <div className="flex items-center space-x-2">
          {user?.role === 'trainer' && (
            <button
              onClick={() => {
                const link = `https://brainathonhub.com/register?ref=${user?.id || ''}`;
                navigator.clipboard.writeText(link);
                toast.success('Referral link copied!');
              }}
              className="flex items-center px-3 py-2 border border-primary-300 text-primary-600 rounded-lg text-sm hover:bg-primary-50"
            >
              <LinkIcon className="w-4 h-4 mr-1" />
              Copy Referral Link
            </button>
          )}
          <button
            onClick={() => setShowOnboardModal(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {user?.role === 'admin' ? 'Onboard Trainer' : 'Onboard Student'}
          </button>
        </div>
      </div>

      {/* L1 Settings Panel */}
      {showL1Settings && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">L1 Trial Configuration</h3>
          <p className="text-xs text-gray-500 mb-3">Set default trial duration and daily question limits for your students.</p>
          <div className="flex items-end space-x-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Trial Days</label>
              <input
                type="number"
                min={1}
                max={90}
                value={l1Config.trial_days}
                onChange={(e) => setL1Config({ ...l1Config, trial_days: parseInt(e.target.value) || 1 })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Daily Limit</label>
              <input
                type="number"
                min={1}
                max={50}
                value={l1Config.daily_limit}
                onChange={(e) => setL1Config({ ...l1Config, daily_limit: parseInt(e.target.value) || 1 })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <button
              onClick={handleSaveL1Config}
              disabled={isSavingL1}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {isSavingL1 ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Quota Info for Trainers */}
      {quota && !quota.unlimited && (
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <MessageSquare className="w-5 h-5 text-primary-600 mr-3" />
            <div>
              <p className="text-primary-900 font-medium">
                {quota.availableQuestions || 0} questions available
              </p>
              <p className="text-primary-600 text-sm">Your AI credit balance</p>
            </div>
          </div>
          {(quota.availableQuestions || 0) < 20 && (
            <Link
              to="/recharge"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
            >
              Recharge
            </Link>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div
          onClick={() => {
            const target = document.getElementById('students-section');
            if (target) target.scrollIntoView({ behavior: 'smooth' });
          }}
          className="bg-white rounded-xl p-6 border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.totalStudents || 0}</p>
          <p className="text-sm text-gray-500">{user?.role === 'admin' ? 'Total Trainers' : 'Total Students'}</p>
        </div>

        <div
          onClick={async () => {
            if (expandedCard === 'monthEarnings') { setExpandedCard(null); return; }
            setExpandedCard('monthEarnings');
            setDrillDownLoading(true);
            try {
              const yearMonth = new Date().toISOString().slice(0, 7);
              const res = await trainerApi.getEarningsBreakdown(yearMonth);
              setDrillDownData(res.data);
            } catch (e) { toast.error('Failed to load earnings breakdown'); }
            finally { setDrillDownLoading(false); }
          }}
          className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${
            expandedCard === 'monthEarnings' ? 'border-green-400 ring-1 ring-green-200' : 'border-gray-200 hover:border-green-300'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            {expandedCard === 'monthEarnings' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">
            ₹{stats?.currentMonth?.recharge_commissions || 0}
          </p>
          <p className="text-sm text-gray-500">This Month's Earnings</p>
        </div>

        <div
          onClick={async () => {
            if (expandedCard === 'totalEarnings') { setExpandedCard(null); return; }
            setExpandedCard('totalEarnings');
            setDrillDownLoading(true);
            try {
              const res = await trainerApi.getEarningsHistory();
              setDrillDownData(res.data);
            } catch (e) { toast.error('Failed to load earnings history'); }
            finally { setDrillDownLoading(false); }
          }}
          className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${
            expandedCard === 'totalEarnings' ? 'border-purple-400 ring-1 ring-purple-200' : 'border-gray-200 hover:border-purple-300'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            {expandedCard === 'totalEarnings' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">₹{stats?.totalEarnings || 0}</p>
          <p className="text-sm text-gray-500">Total Earnings</p>
        </div>

        <div
          onClick={async () => {
            if (expandedCard === 'pendingPayout') { setExpandedCard(null); return; }
            setExpandedCard('pendingPayout');
            setDrillDownLoading(true);
            try {
              const res = await trainerApi.getPayoutDetails();
              setDrillDownData(res.data);
            } catch (e) { toast.error('Failed to load payout details'); }
            finally { setDrillDownLoading(false); }
          }}
          className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${
            expandedCard === 'pendingPayout' ? 'border-orange-400 ring-1 ring-orange-200' : 'border-gray-200 hover:border-orange-300'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
            {expandedCard === 'pendingPayout' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">₹{stats?.pendingPayouts || 0}</p>
          <p className="text-sm text-gray-500">Pending Payout</p>
        </div>
      </div>

      {/* Drill-Down Panel */}
      {expandedCard && (
        <div className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {drillDownLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : expandedCard === 'monthEarnings' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-700">This Month's Earnings Breakdown</h3>
              </div>
              {drillDownData.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Recharge Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Your Commission</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {drillDownData.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.student_name}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">₹{item.amount}</td>
                        <td className="px-4 py-2 text-sm text-green-600 font-medium">₹{item.trainer_share}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{new Date(item.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-gray-500">No earnings this month yet</div>
              )}
            </div>
          ) : expandedCard === 'totalEarnings' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-700">Monthly Earnings History</h3>
              </div>
              {drillDownData.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Commissions</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Students Onboarded</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payout Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {drillDownData.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.year_month}</td>
                        <td className="px-4 py-2 text-sm text-green-600 font-medium">₹{item.recharge_commissions}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{item.students_onboarded || 0}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            item.payout_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {item.payout_status === 'paid' ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-gray-500">No earnings history</div>
              )}
            </div>
          ) : expandedCard === 'pendingPayout' && drillDownData ? (
            <div className="p-6">
              <h3 className="font-semibold text-gray-700 mb-3">Payout Details</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Method:</span> <span className="font-medium">{drillDownData.payout_method === 'bank' ? 'Bank Transfer' : 'UPI'}</span></p>
                {drillDownData.upi_id && <p><span className="text-gray-500">UPI ID:</span> <span className="font-medium">{drillDownData.upi_id}</span></p>}
                {drillDownData.bank_name && <p><span className="text-gray-500">Bank:</span> <span className="font-medium">{drillDownData.bank_name}</span></p>}
                {drillDownData.account_number && <p><span className="text-gray-500">Account:</span> <span className="font-medium">{drillDownData.account_number}</span></p>}
                <p><span className="text-gray-500">Verified:</span> <span className={`font-medium ${drillDownData.is_verified ? 'text-green-600' : 'text-amber-600'}`}>{drillDownData.is_verified ? 'Yes' : 'Not yet'}</span></p>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No data available</div>
          )}
        </div>
      )}

      {/* Students Table */}
      <div id="students-section" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">{user?.role === 'admin' ? 'Your Trainers' : 'Your Students'}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions Left</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {students.length > 0 ? (
                students.map((student) => (
                  <tr key={student.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{student.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        student.tier === 'L1'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {student.tier}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{student.available_questions}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(student.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        student.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                        student.status === 'archived' ? 'bg-red-100 text-red-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {student.status === 'paused' ? 'Paused' :
                         student.status === 'archived' ? 'Archived' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        {student.tier === 'L2' && (
                          <button
                            onClick={() => handleGrantL1(student.id)}
                            className="text-xs text-primary-600 hover:underline whitespace-nowrap mr-1"
                          >
                            Grant L1
                          </button>
                        )}
                        {(!student.status || student.status === 'active') && (
                          <>
                            <button
                              onClick={() => handleStudentStatus(student.id, 'paused')}
                              title="Pause"
                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            >
                              <Pause className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleStudentStatus(student.id, 'archived')}
                              title="Pause & Archive"
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Archive className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        {(student.status === 'paused' || student.status === 'archived') && (
                          <button
                            onClick={() => handleStudentStatus(student.id, 'active')}
                            title="Reactivate"
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            <Play className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <a
                          href={`mailto:${student.email}`}
                          title="Send Email"
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                        {student.phone && (
                          <a
                            href={getWhatsAppUrl(student.phone)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="WhatsApp"
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    {user?.role === 'admin'
                      ? 'No trainers yet. Click "Onboard Trainer" to add your first trainer.'
                      : 'No students yet. Click "Onboard Student" to add your first student.'
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Offers */}
      {offers.length > 0 && (
        <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Your Offers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchased</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {offers.map((offer) => (
                  <tr key={offer.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{offer.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">₹{offer.amount}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{offer.questions}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{offer.recipient_count}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{offer.purchased_count || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        offer.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Onboard Modal */}
      {showOnboardModal && (
        <OnboardStudentModal
          onClose={() => setShowOnboardModal(false)}
          onSuccess={() => {
            setShowOnboardModal(false);
            loadData();
          }}
          isAdmin={user?.role === 'admin'}
        />
      )}

      {/* Create Offer Modal */}
      {showOfferModal && (
        <CreateOfferModal
          students={students}
          onClose={() => setShowOfferModal(false)}
          onSuccess={() => {
            setShowOfferModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function OnboardStudentModal({ onClose, onSuccess, isAdmin }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      if (isAdmin) {
        await adminApi.createTrainer(formData);
        toast.success('Trainer created successfully!');
        onSuccess();
      } else {
        const res = await trainerApi.createOnboardingOrder(formData);
        const { paymentUrl } = res.data;
        window.location.href = paymentUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.error || (isAdmin ? 'Failed to create trainer' : 'Failed to initiate payment'));
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">{isAdmin ? 'Onboard New Trainer' : 'Onboard New Student'}</h2>
        <p className="text-gray-600 text-sm mb-4">
          {isAdmin
            ? 'Fill in the trainer details below. A login credential email will be sent to the trainer.'
            : 'Fill in the student details below. You will be charged ₹845 (incl. 200 Questions).'
          }
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{isAdmin ? 'Password' : 'Initial Password'}</label>
            <input
              type="password"
              required
              minLength={6}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isProcessing
                ? (isAdmin ? 'Creating Trainer...' : 'Redirecting to Payment...')
                : (isAdmin ? 'Create Trainer' : 'Pay ₹845 & Onboard (incl. 200 Questions)')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateOfferModal({ students, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    questions: '',
    studentIds: [],
    expiresAt: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleStudent = (id) => {
    setFormData(prev => ({
      ...prev,
      studentIds: prev.studentIds.includes(id)
        ? prev.studentIds.filter(s => s !== id)
        : [...prev.studentIds, id]
    }));
  };

  const selectAll = () => {
    if (formData.studentIds.length === students.length) {
      setFormData(prev => ({ ...prev, studentIds: [] }));
    } else {
      setFormData(prev => ({ ...prev, studentIds: students.map(s => s.id) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.studentIds.length === 0) {
      toast.error('Select at least one student');
      return;
    }
    setIsSubmitting(true);
    try {
      await offerApi.create({
        name: formData.name,
        description: formData.description,
        amount: parseFloat(formData.amount),
        questions: parseInt(formData.questions),
        studentIds: formData.studentIds,
        expiresAt: formData.expiresAt || null
      });
      toast.success('Offer created and sent to students');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Create Offer</h2>
        <p className="text-gray-600 text-sm mb-4">
          Create a special recharge offer for your students.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Offer Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Special Weekend Offer"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
              <input
                type="number"
                required
                min={1}
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Questions</label>
              <input
                type="number"
                required
                min={1}
                value={formData.questions}
                onChange={(e) => setFormData({ ...formData, questions: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expires (optional)</label>
            <input
              type="datetime-local"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Select Students</label>
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-primary-600 hover:underline"
              >
                {formData.studentIds.length === students.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
              {students.map(student => (
                <label key={student.id} className="flex items-center space-x-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.studentIds.includes(student.id)}
                    onChange={() => toggleStudent(student.id)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{student.name}</span>
                  <span className="text-xs text-gray-400">{student.email}</span>
                </label>
              ))}
              {students.length === 0 && (
                <p className="text-sm text-gray-500 p-2 text-center">No students available</p>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">{formData.studentIds.length} selected</p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Offer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TrainerDashboard;
